import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToDDLCollections,
  getDDLCollections,
  updateDDLCollection,
  deleteDDLCollection,
  sortDDLItems,
  getCollectionDepthTag,
} from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import AssignDDLModal from './AssignDDLModal';
import './DDLDetail.css';

export default function DDLDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [allCollections, setAllCollections] = useState([]);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selection state — use labels instead of indices to avoid sort mismatch
  const [selectedPrimaryLabel, setSelectedPrimaryLabel] = useState(null);
  const [selectedSecondaryLabel, setSelectedSecondaryLabel] = useState(null);

  // Inline add
  const [addingLevel, setAddingLevel] = useState(null); // 'primary' | 'secondary' | 'tertiary'
  const [addText, setAddText] = useState('');
  const addRef = useRef(null);

  // Inline edit
  const [editingItem, setEditingItem] = useState(null); // { level, label, text }
  const editRef = useRef(null);
  // Tracks whether we've already positioned the cursor for the current edit session
  const editFocusedRef = useRef(false);

  // Expiry date editing — { level, label } of the item whose date picker is open
  const [editingExpiry, setEditingExpiry] = useState(null);
  const [expiryText, setExpiryText] = useState('');

  // Rename collection
  const [renaming, setRenaming] = useState(false);
  const [renameText, setRenameText] = useState('');
  const renameRef = useRef(null);

  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Permission guard
  const [showDDLDenied, setShowDDLDenied] = useState(false);
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);

  function guardDDL(action) {
    if (perms.canManageDDL) {
      action();
    } else {
      setShowDDLDenied(true);
    }
  }

  // Real-time listener for all collections
  useEffect(() => {
    const unsubscribe = subscribeToDDLCollections((list) => {
      setAllCollections(list);
      const found = list.find((c) => c.id === id);
      setCollection(found || null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [id]);

  // Focus add input when addingLevel changes
  useEffect(() => {
    if (addingLevel && addRef.current) addRef.current.focus();
  }, [addingLevel]);

  // Focus edit input once when a new edit session starts.
  // We use editFocusedRef to ensure setSelectionRange only fires on the initial
  // open — NOT on every subsequent keystroke (which would jump the cursor to end).
  useEffect(() => {
    if (editingItem) {
      // New edit session started — reset the guard
      editFocusedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem?.label, editingItem?.level]); // only when the identity of the item changes

  useEffect(() => {
    if (editingItem && editRef.current && !editFocusedRef.current) {
      editFocusedRef.current = true;
      editRef.current.focus();
      const len = editRef.current.value.length;
      editRef.current.setSelectionRange(len, len);
    }
  });

  useEffect(() => {
    if (renaming && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [renaming]);

  // ─── Helpers ───────────────────────────────────────────────────

  function getRawItems() {
    return collection?.items || [];
  }

  function getPrimaryItems() {
    return sortDDLItems(getRawItems());
  }

  function findRawPrimary(label) {
    return getRawItems().find((it) => it.label === label) || null;
  }

  function findRawSecondary(primaryLabel, secondaryLabel) {
    const primary = findRawPrimary(primaryLabel);
    if (!primary || !primary.children) return null;
    return primary.children.find((it) => it.label === secondaryLabel) || null;
  }

  function getSecondaryItems() {
    if (!selectedPrimaryLabel) return [];
    const primary = findRawPrimary(selectedPrimaryLabel);
    return sortDDLItems(primary?.children || []);
  }

  function getTertiaryItems() {
    if (!selectedPrimaryLabel || !selectedSecondaryLabel) return [];
    const secondary = findRawSecondary(selectedPrimaryLabel, selectedSecondaryLabel);
    return sortDDLItems(secondary?.children || []);
  }

  function findRawIndex(arr, label) {
    return arr.findIndex((it) => it.label === label);
  }

  // Force a refresh from the server, bypassing the 15s poll. Keeps the UI
  // snappy after add/edit/delete/expiry/rename.
  async function refreshFromServer() {
    try {
      const list = await getDDLCollections();
      setAllCollections(list);
      const found = list.find((c) => c.id === id);
      if (found) setCollection(found);
    } catch (err) {
      console.error('Failed to refresh DDL collections:', err);
    }
  }

  async function saveItems(newItems) {
    await updateDDLCollection(collection.id, { items: newItems });
    await refreshFromServer();
  }

  // ─── Add item ──────────────────────────────────────────────────

  function startAdding(level) {
    setAddingLevel(level);
    setAddText('');
    setEditingItem(null);
  }

  async function handleAdd() {
    // No case transformation — save exactly as typed
    const label = addText.trim();
    if (!label) return;

    const items = JSON.parse(JSON.stringify(getRawItems()));

    if (addingLevel === 'primary') {
      if (items.some((it) => it.label === label)) { setAddText(''); return; }
      items.push({ label, children: [] });
      await saveItems(sortDDLItems(items));
    } else if (addingLevel === 'secondary' && selectedPrimaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) return;
      const children = items[pIdx].children || [];
      if (children.some((it) => it.label === label)) { setAddText(''); return; }
      children.push({ label, children: [] });
      items[pIdx].children = sortDDLItems(children);
      await saveItems(items);
    } else if (addingLevel === 'tertiary' && selectedPrimaryLabel && selectedSecondaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) return;
      const sIdx = findRawIndex(items[pIdx].children || [], selectedSecondaryLabel);
      if (sIdx < 0) return;
      const children = items[pIdx].children[sIdx].children || [];
      if (children.some((it) => it.label === label)) { setAddText(''); return; }
      children.push({ label, children: [], expiryDate: null });
      items[pIdx].children[sIdx].children = sortDDLItems(children);
      await saveItems(items);
    }

    setAddText('');
    if (addRef.current) addRef.current.focus();
  }

  // ─── Edit item ─────────────────────────────────────────────────

  function startEditing(level, currentLabel) {
    setEditingItem({ level, label: currentLabel, text: currentLabel });
    setAddingLevel(null);
  }

  async function handleSaveEdit() {
    if (!editingItem) return;
    // No case transformation — save exactly as typed
    const newLabel = editingItem.text.trim();
    if (!newLabel) { setEditingItem(null); return; }

    const items = JSON.parse(JSON.stringify(getRawItems()));
    const { level, label: oldLabel } = editingItem;

    if (level === 'primary') {
      const idx = findRawIndex(items, oldLabel);
      if (idx < 0) { setEditingItem(null); return; }
      if (newLabel !== oldLabel && items.some((it) => it.label === newLabel)) {
        setEditingItem(null); return;
      }
      items[idx].label = newLabel;
      await saveItems(sortDDLItems(items));
      setSelectedPrimaryLabel(newLabel);
      setSelectedSecondaryLabel(null);
    } else if (level === 'secondary' && selectedPrimaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) { setEditingItem(null); return; }
      const children = items[pIdx].children;
      const sIdx = findRawIndex(children, oldLabel);
      if (sIdx < 0) { setEditingItem(null); return; }
      if (newLabel !== oldLabel && children.some((it) => it.label === newLabel)) {
        setEditingItem(null); return;
      }
      children[sIdx].label = newLabel;
      items[pIdx].children = sortDDLItems(children);
      await saveItems(items);
      setSelectedSecondaryLabel(newLabel);
    } else if (level === 'tertiary' && selectedPrimaryLabel && selectedSecondaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) { setEditingItem(null); return; }
      const sIdx = findRawIndex(items[pIdx].children || [], selectedSecondaryLabel);
      if (sIdx < 0) { setEditingItem(null); return; }
      const children = items[pIdx].children[sIdx].children;
      const tIdx = findRawIndex(children, oldLabel);
      if (tIdx < 0) { setEditingItem(null); return; }
      if (newLabel !== oldLabel && children.some((it) => it.label === newLabel)) {
        setEditingItem(null); return;
      }
      children[tIdx].label = newLabel;
      items[pIdx].children[sIdx].children = sortDDLItems(children);
      await saveItems(items);
    }

    setEditingItem(null);
  }

  // ─── Delete item ───────────────────────────────────────────────

  async function handleDelete(level, label) {
    const items = JSON.parse(JSON.stringify(getRawItems()));

    if (level === 'primary') {
      const confirmed = window.confirm(`Delete "${label}" and all its sub-items?`);
      if (!confirmed) return;
      const idx = findRawIndex(items, label);
      if (idx < 0) return;
      items.splice(idx, 1);
      if (selectedPrimaryLabel === label) {
        setSelectedPrimaryLabel(null);
        setSelectedSecondaryLabel(null);
      }
    } else if (level === 'secondary' && selectedPrimaryLabel) {
      const confirmed = window.confirm(`Delete "${label}" and all its sub-items?`);
      if (!confirmed) return;
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) return;
      const sIdx = findRawIndex(items[pIdx].children || [], label);
      if (sIdx < 0) return;
      items[pIdx].children.splice(sIdx, 1);
      if (selectedSecondaryLabel === label) {
        setSelectedSecondaryLabel(null);
      }
    } else if (level === 'tertiary' && selectedPrimaryLabel && selectedSecondaryLabel) {
      const confirmed = window.confirm(`Delete "${label}"?`);
      if (!confirmed) return;
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx < 0) return;
      const sIdx = findRawIndex(items[pIdx].children || [], selectedSecondaryLabel);
      if (sIdx < 0) return;
      const tIdx = findRawIndex(items[pIdx].children[sIdx].children || [], label);
      if (tIdx < 0) return;
      items[pIdx].children[sIdx].children.splice(tIdx, 1);
    }

    await saveItems(items);
  }

  // ─── Expiry date (deepest level only) ──────────────────────────

  function startEditingExpiry(level, label, currentExpiry) {
    setEditingExpiry({ level, label });
    setExpiryText(currentExpiry || '');
  }

  async function handleSaveExpiry() {
    if (!editingExpiry) return;
    const { level, label } = editingExpiry;
    const items = JSON.parse(JSON.stringify(getRawItems()));

    if (level === 'primary') {
      const idx = findRawIndex(items, label);
      if (idx >= 0) items[idx].expiryDate = expiryText || null;
    } else if (level === 'secondary' && selectedPrimaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx >= 0) {
        const sIdx = findRawIndex(items[pIdx].children || [], label);
        if (sIdx >= 0) items[pIdx].children[sIdx].expiryDate = expiryText || null;
      }
    } else if (level === 'tertiary' && selectedPrimaryLabel && selectedSecondaryLabel) {
      const pIdx = findRawIndex(items, selectedPrimaryLabel);
      if (pIdx >= 0) {
        const sIdx = findRawIndex(items[pIdx].children || [], selectedSecondaryLabel);
        if (sIdx >= 0) {
          const tIdx = findRawIndex(items[pIdx].children[sIdx].children || [], label);
          if (tIdx >= 0) items[pIdx].children[sIdx].children[tIdx].expiryDate = expiryText || null;
        }
      }
    }

    await saveItems(items);
    setEditingExpiry(null);
    setExpiryText('');
  }

  function getExpiryForItem(level, label) {
    const rawItems = getRawItems();
    if (level === 'primary') {
      const it = rawItems.find((x) => x.label === label);
      return it?.expiryDate || null;
    } else if (level === 'secondary' && selectedPrimaryLabel) {
      const prim = rawItems.find((x) => x.label === selectedPrimaryLabel);
      const it = (prim?.children || []).find((x) => x.label === label);
      return it?.expiryDate || null;
    } else if (level === 'tertiary' && selectedPrimaryLabel && selectedSecondaryLabel) {
      const prim = rawItems.find((x) => x.label === selectedPrimaryLabel);
      const sec = (prim?.children || []).find((x) => x.label === selectedSecondaryLabel);
      const it = (sec?.children || []).find((x) => x.label === label);
      return it?.expiryDate || null;
    }
    return null;
  }

  function formatExpiryDisplay(dateStr) {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  function isExpiringSoon(dateStr) {
    if (!dateStr) return false;
    const d = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff >= 0;
  }

  function isExpired(dateStr) {
    if (!dateStr) return false;
    const d = new Date(dateStr + 'T00:00:00');
    return d < new Date();
  }

  // ─── Rename collection ─────────────────────────────────────────

  function startRenaming() {
    setRenaming(true);
    setRenameText(collection.name);
  }

  async function handleSaveRename() {
    const name = renameText.trim();
    if (!name || name === collection.name) { setRenaming(false); return; }
    await updateDDLCollection(collection.id, { name });
    await refreshFromServer();
    setRenaming(false);
  }

  // ─── Delete collection ─────────────────────────────────────────

  async function handleDeleteCollection() {
    const confirmed = window.confirm(`Delete "${collection.name}" and all its data? This cannot be undone.`);
    if (!confirmed) return;
    await deleteDDLCollection(collection.id);
    navigate('/ddl-collection');
  }

  // ─── Render helpers ────────────────────────────────────────────

  function renderExpiryBadge(level, label) {
    const expiry = getExpiryForItem(level, label);
    const expired = isExpired(expiry);
    const soon = isExpiringSoon(expiry);
    const display = formatExpiryDisplay(expiry);

    if (editingExpiry && editingExpiry.level === level && editingExpiry.label === label) {
      return (
        <div className="ddl-expiry-edit-row" onClick={(e) => e.stopPropagation()}>
          <input
            type="date"
            className="ddl-expiry-input"
            value={expiryText}
            onChange={(e) => setExpiryText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveExpiry();
              if (e.key === 'Escape') { setEditingExpiry(null); setExpiryText(''); }
            }}
            autoFocus
          />
          <button className="ddl-expiry-save-btn" onClick={handleSaveExpiry}>✓</button>
          {expiry && (
            <button
              className="ddl-expiry-clear-btn"
              title="Clear date"
              onClick={() => { setExpiryText(''); handleSaveExpiry(); }}
            >✕</button>
          )}
        </div>
      );
    }

    return (
      <button
        className={`ddl-expiry-badge${expired ? ' ddl-expiry-expired' : soon ? ' ddl-expiry-soon' : expiry ? ' ddl-expiry-set' : ' ddl-expiry-none'}`}
        title={expired ? 'Expired' : soon ? 'Expiring soon' : expiry ? 'Has expiry date' : 'Set expiry date'}
        onClick={(e) => { e.stopPropagation(); guardDDL(() => startEditingExpiry(level, label, expiry)); }}
      >
        {display
          ? <>{expired ? '⚠ ' : soon ? '⏰ ' : '📅 '}{display}</>
          : <span className="ddl-expiry-placeholder">+ Expiry</span>
        }
      </button>
    );
  }

  // ─── Render ────────────────────────────────────────────────────

  if (loading) {
    return <div className="ddl-detail"><div className="ddl-empty"><p>Loading...</p></div></div>;
  }

  if (!collection) {
    return (
      <div className="ddl-detail">
        <div className="ddl-empty">
          <p>Collection not found.</p>
          <button className="btn btn-primary" onClick={() => navigate('/ddl-collection')}>Back to Collections</button>
        </div>
      </div>
    );
  }

  const items = getRawItems();
  const depthTag = getCollectionDepthTag(items);
  const deepestLevel = depthTag.toLowerCase(); // where expiry is shown
  const isAssigned = !!collection.assignedTo;
  const primaryItems = getPrimaryItems();
  const secondaryItems = getSecondaryItems();
  const tertiaryItems = getTertiaryItems();

  // Chevron navigation
  const sortedCollections = [...allCollections].sort((a, b) => a.name.localeCompare(b.name));
  const currentIndex = sortedCollections.findIndex((c) => c.id === id);
  const prevCollection = currentIndex > 0 ? sortedCollections[currentIndex - 1] : null;
  const nextCollection = currentIndex < sortedCollections.length - 1 ? sortedCollections[currentIndex + 1] : null;
  const showChevrons = sortedCollections.length > 1;

  // Expiry applies on the single‑level case too: if collection is primary-only
  // it shows on primary items; if secondary-only on secondary; if tertiary on tertiary.
  const showExpiryOnPrimary   = deepestLevel === 'primary';
  const showExpiryOnSecondary = deepestLevel === 'secondary';
  const showExpiryOnTertiary  = deepestLevel === 'tertiary';

  return (
    <div className="ddl-detail">
      {/* Header */}
      <div className="ddl-detail-header">
        <button className="btn-back" onClick={() => navigate('/ddl-collection')}>
          ← Back to Collections
        </button>
        <div className="ddl-detail-title-row">
          {renaming ? (
            <input
              ref={renameRef}
              className="ddl-detail-rename-input"
              value={renameText}
              onChange={(e) => setRenameText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') setRenaming(false);
              }}
              onBlur={handleSaveRename}
            />
          ) : (
            <h1 className="ddl-detail-title" onDoubleClick={startRenaming}>
              {collection.name}
            </h1>
          )}
          <span className={`ddl-depth-tag ddl-depth-${depthTag.toLowerCase()}`}>
            {depthTag}
          </span>
          <button
            className={`ddl-status-circle${isAssigned ? ' ddl-status-assigned' : ''}`}
            onClick={() => setShowAssignModal(true)}
            title={isAssigned ? 'Assigned — click to manage' : 'Not assigned — click to assign'}
          >
            {isAssigned && (
              <svg viewBox="0 0 16 16" className="ddl-status-tick">
                <polyline points="3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          <div className="ddl-detail-actions">
            {showChevrons && (
              <div className="nav-chevrons">
                {prevCollection ? (
                  <button className="nav-chevron" onClick={() => navigate(`/ddl-collection/${prevCollection.id}`)} title={prevCollection.name}>&lsaquo;</button>
                ) : <span className="nav-chevron-hidden">&lsaquo;</span>}
                {nextCollection ? (
                  <button className="nav-chevron" onClick={() => navigate(`/ddl-collection/${nextCollection.id}`)} title={nextCollection.name}>&rsaquo;</button>
                ) : <span className="nav-chevron-hidden">&rsaquo;</span>}
              </div>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => guardDDL(startRenaming)}>
              Rename
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => guardDDL(handleDeleteCollection)}>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* 3-column hierarchy */}
      <div className="ddl-columns">
        {/* Primary column */}
        <div className="ddl-column">
          <div className="ddl-column-header">Primary</div>
          <div className="ddl-column-body">
            {primaryItems.length === 0 && addingLevel !== 'primary' && (
              <div className="ddl-column-empty">No items yet</div>
            )}
            {primaryItems.map((item) => (
              <div
                key={item.label}
                className={`ddl-col-item${selectedPrimaryLabel === item.label ? ' ddl-col-item-selected' : ''}`}
                onClick={() => { setSelectedPrimaryLabel(item.label); setSelectedSecondaryLabel(null); setAddingLevel(null); setEditingItem(null); setEditingExpiry(null); }}
              >
                {editingItem && editingItem.level === 'primary' && editingItem.label === item.label ? (
                  <input
                    ref={editRef}
                    className="ddl-col-edit-input"
                    value={editingItem.text}
                    onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingItem(null); }}
                    onBlur={handleSaveEdit}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <>
                    <span className="ddl-col-item-label">{item.label}</span>
                    <div className="ddl-col-item-actions">
                      {showExpiryOnPrimary && renderExpiryBadge('primary', item.label)}
                      <button className="ddl-col-action-btn" onClick={(e) => { e.stopPropagation(); guardDDL(() => startEditing('primary', item.label)); }} title="Edit">✏️</button>
                      <button className="ddl-col-action-btn ddl-col-action-delete" onClick={(e) => { e.stopPropagation(); guardDDL(() => handleDelete('primary', item.label)); }} title="Delete">✕</button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {addingLevel === 'primary' ? (
              <div className="ddl-col-add-row">
                <input ref={addRef} className="ddl-col-add-input" placeholder="Enter value..." value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAddingLevel(null); }}
                />
                <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!addText.trim()}>Add</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setAddingLevel(null)}>Done</button>
              </div>
            ) : (
              <button className="ddl-col-add-btn" onClick={() => guardDDL(() => startAdding('primary'))}>+ Add Item</button>
            )}
          </div>
        </div>

        {/* Secondary column */}
        {selectedPrimaryLabel !== null && (
          <div className="ddl-column">
            <div className="ddl-column-header">Secondary</div>
            <div className="ddl-column-body">
              {secondaryItems.length === 0 && addingLevel !== 'secondary' && (
                <div className="ddl-column-empty">No items yet</div>
              )}
              {secondaryItems.map((item) => (
                <div
                  key={item.label}
                  className={`ddl-col-item${selectedSecondaryLabel === item.label ? ' ddl-col-item-selected' : ''}`}
                  onClick={() => { setSelectedSecondaryLabel(item.label); setAddingLevel(null); setEditingItem(null); setEditingExpiry(null); }}
                >
                  {editingItem && editingItem.level === 'secondary' && editingItem.label === item.label ? (
                    <input
                      ref={editRef}
                      className="ddl-col-edit-input"
                      value={editingItem.text}
                      onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingItem(null); }}
                      onBlur={handleSaveEdit}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <span className="ddl-col-item-label">{item.label}</span>
                      <div className="ddl-col-item-actions">
                        {showExpiryOnSecondary && renderExpiryBadge('secondary', item.label)}
                        <button className="ddl-col-action-btn" onClick={(e) => { e.stopPropagation(); guardDDL(() => startEditing('secondary', item.label)); }} title="Edit">✏️</button>
                        <button className="ddl-col-action-btn ddl-col-action-delete" onClick={(e) => { e.stopPropagation(); guardDDL(() => handleDelete('secondary', item.label)); }} title="Delete">✕</button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {addingLevel === 'secondary' ? (
                <div className="ddl-col-add-row">
                  <input ref={addRef} className="ddl-col-add-input" placeholder="Enter value..." value={addText}
                    onChange={(e) => setAddText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAddingLevel(null); }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!addText.trim()}>Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddingLevel(null)}>Done</button>
                </div>
              ) : (
                <button className="ddl-col-add-btn" onClick={() => guardDDL(() => startAdding('secondary'))}>+ Add Item</button>
              )}
            </div>
          </div>
        )}

        {/* Tertiary column */}
        {selectedPrimaryLabel !== null && selectedSecondaryLabel !== null && (
          <div className="ddl-column">
            <div className="ddl-column-header">Tertiary</div>
            <div className="ddl-column-body">
              {tertiaryItems.length === 0 && addingLevel !== 'tertiary' && (
                <div className="ddl-column-empty">No items yet</div>
              )}
              {tertiaryItems.map((item) => (
                <div key={item.label} className="ddl-col-item">
                  {editingItem && editingItem.level === 'tertiary' && editingItem.label === item.label ? (
                    <input
                      ref={editRef}
                      className="ddl-col-edit-input"
                      value={editingItem.text}
                      onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditingItem(null); }}
                      onBlur={handleSaveEdit}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <span className="ddl-col-item-label">{item.label}</span>
                      <div className="ddl-col-item-actions">
                        {showExpiryOnTertiary && renderExpiryBadge('tertiary', item.label)}
                        <button className="ddl-col-action-btn" onClick={(e) => { e.stopPropagation(); guardDDL(() => startEditing('tertiary', item.label)); }} title="Edit">✏️</button>
                        <button className="ddl-col-action-btn ddl-col-action-delete" onClick={(e) => { e.stopPropagation(); guardDDL(() => handleDelete('tertiary', item.label)); }} title="Delete">✕</button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {addingLevel === 'tertiary' ? (
                <div className="ddl-col-add-row">
                  <input ref={addRef} className="ddl-col-add-input" placeholder="Enter value..." value={addText}
                    onChange={(e) => setAddText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAddingLevel(null); }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!addText.trim()}>Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddingLevel(null)}>Done</button>
                </div>
              ) : (
                <button className="ddl-col-add-btn" onClick={() => guardDDL(() => startAdding('tertiary'))}>+ Add Item</button>
              )}
            </div>
          </div>
        )}
      </div>

      {showAssignModal && (
        <AssignDDLModal
          collection={collection}
          allCollections={allCollections}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {showDDLDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowDDLDenied(false)}
        />
      )}
    </div>
  );
}
